/**
 * ArmFallbackDetector — 박하연 연구원 0513 피드백 반영
 *
 * MediaPipe HandLandmarker가 손을 못 잡는 경우(프레임 가장자리, 책상
 * 아래, 옷에 가림, 손가락이 카메라 향함 등)에 **PoseLandmarker 33점의
 * 어깨·팔꿈치·손목**을 보조 신호로 사용해서 손의 대략 위치와 팔 자세를
 * 추정한다.
 *
 * 정책 정합:
 *   - 추정 boolean 단정 ❌ — visibility·confidence를 함께 노출
 *     (AI Proctor 6원칙 #2, 안정성 PDF #7 증거 설명성)
 *   - 손이 직접 보이면 본 모듈은 비활성 — 우선순위는 HandLandmarker
 *   - "팔 랜드마크 기반 추정"이라는 사실을 narrator에 명시 (워딩 중립)
 *
 * Pose 33-keypoint topology에서 본 모듈이 사용하는 점:
 *   11 left shoulder    12 right shoulder
 *   13 left elbow       14 right elbow
 *   15 left wrist       16 right wrist
 */

export interface PoseLandmark {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}

const POSE_LM = {
  LEFT_SHOULDER:  11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW:     13,
  RIGHT_ELBOW:    14,
  LEFT_WRIST:     15,
  RIGHT_WRIST:    16,
} as const;

const VISIBILITY_THRESHOLD = 0.45;

export type ArmSide = 'left' | 'right';

export type ArmPosture =
  | 'extended_forward'   // 팔꿈치가 거의 펴짐 + wrist가 어깨 앞쪽 — 책상에 손을 뻗음
  | 'bent_writing'       // 팔꿈치 < 110° + wrist가 어깨 아래 — 글쓰기 자세
  | 'raised'             // wrist y < shoulder y — 손을 올림(휴대폰 들기 등)
  | 'lowered'            // wrist가 화면 하단 가까이 — 책상 아래
  | 'unknown';

export interface BBox {
  /** Normalized [0,1] coords. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ArmEstimate {
  side: ArmSide;
  shoulder: { x: number; y: number; visibility: number };
  elbow:    { x: number; y: number; visibility: number };
  wrist:    { x: number; y: number; visibility: number };
  /** Elbow joint angle in degrees (0 = fully closed, 180 = straight). */
  elbowAngleDeg: number;
  posture: ArmPosture;
  /** Forward-extrapolated zone where the hidden hand likely sits. */
  estimatedHandZone: BBox;
  /** Mean visibility of the 3 joints, used as the estimate confidence. */
  confidence: number;
}

export interface ArmFallbackResult {
  /** True iff hand is NOT directly detected AND at least one arm chain is visible. */
  active: boolean;
  /** Primary arm estimate — highest confidence chain. null when no arm visible. */
  primary: ArmEstimate | null;
  /** Both arms when both chains are visible (e.g., 양손 협응 추정 후보). */
  arms: ArmEstimate[];
  /** narrator 워딩 (중립, "추정 중"·"확인 불가" 표현). */
  narratorText: string;
  /** UI color hint, mirrors PartialVisibilityClassifier convention. */
  uiColor: 'green' | 'yellow' | 'orange' | 'red';
}

export interface ArmFallbackInput {
  /** HandLandmarker가 손을 잡았는가. true면 본 모듈 결과는 무시되어야 함. */
  handDetected: boolean;
  /** Pose 33-keypoint landmarks (정규화 좌표 + visibility). */
  poseLandmarks: PoseLandmark[] | null | undefined;
}

const INACTIVE: ArmFallbackResult = {
  active: false,
  primary: null,
  arms: [],
  narratorText: '',
  uiColor: 'green',
};

export function classifyArmFallback(input: ArmFallbackInput): ArmFallbackResult {
  // Priority: real hand detection wins. ArmFallback only fires when hand is lost.
  if (input.handDetected) return INACTIVE;
  const lms = input.poseLandmarks;
  if (!lms || lms.length < 17) {
    return {
      active: false,
      primary: null,
      arms: [],
      narratorText: '손·팔 모두 추적 불가',
      uiColor: 'red',
    };
  }

  const left = buildArm('left', lms);
  const right = buildArm('right', lms);
  const arms = [left, right].filter((a): a is ArmEstimate => a !== null);

  if (arms.length === 0) {
    return {
      active: false,
      primary: null,
      arms: [],
      narratorText: '손·팔 모두 추적 불가',
      uiColor: 'red',
    };
  }

  // Pick the highest-confidence arm as primary.
  arms.sort((a, b) => b.confidence - a.confidence);
  const primary = arms[0];

  return {
    active: true,
    primary,
    arms,
    narratorText: narrate(primary, arms.length),
    uiColor: confidenceColor(primary.confidence),
  };
}

// ─── Internal ──────────────────────────────────────────────────

function buildArm(side: ArmSide, lms: PoseLandmark[]): ArmEstimate | null {
  const idxShoulder = side === 'left' ? POSE_LM.LEFT_SHOULDER : POSE_LM.RIGHT_SHOULDER;
  const idxElbow    = side === 'left' ? POSE_LM.LEFT_ELBOW    : POSE_LM.RIGHT_ELBOW;
  const idxWrist    = side === 'left' ? POSE_LM.LEFT_WRIST    : POSE_LM.RIGHT_WRIST;

  const s = lms[idxShoulder];
  const e = lms[idxElbow];
  const w = lms[idxWrist];
  if (!s || !e || !w) return null;
  const sv = s.visibility ?? 1.0;
  const ev = e.visibility ?? 1.0;
  const wv = w.visibility ?? 1.0;
  // Need at least shoulder + elbow above threshold to extrapolate hand zone;
  // wrist itself can be borderline (it's often near where the hand is hidden).
  if (sv < VISIBILITY_THRESHOLD || ev < VISIBILITY_THRESHOLD) return null;

  const elbowAngleDeg = jointAngleDeg(s, e, w);
  const posture = classifyPosture(s, e, w, elbowAngleDeg);
  const estimatedHandZone = forwardExtrapolate(e, w);
  const confidence = (sv + ev + wv) / 3;

  return {
    side,
    shoulder: { x: s.x, y: s.y, visibility: sv },
    elbow:    { x: e.x, y: e.y, visibility: ev },
    wrist:    { x: w.x, y: w.y, visibility: wv },
    elbowAngleDeg,
    posture,
    estimatedHandZone,
    confidence,
  };
}

function jointAngleDeg(
  s: PoseLandmark, e: PoseLandmark, w: PoseLandmark,
): number {
  const v1x = s.x - e.x, v1y = s.y - e.y;
  const v2x = w.x - e.x, v2y = w.y - e.y;
  const dot = v1x * v2x + v1y * v2y;
  const n1 = Math.hypot(v1x, v1y);
  const n2 = Math.hypot(v2x, v2y);
  if (n1 === 0 || n2 === 0) return 180;
  const cos = Math.max(-1, Math.min(1, dot / (n1 * n2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

function classifyPosture(
  s: PoseLandmark, _e: PoseLandmark, w: PoseLandmark, elbowAngleDeg: number,
): ArmPosture {
  if (w.y < s.y - 0.05) return 'raised';
  if (w.y > 0.85) return 'lowered';
  if (elbowAngleDeg >= 150) return 'extended_forward';
  if (elbowAngleDeg <= 110) return 'bent_writing';
  return 'unknown';
}

/**
 * Forward-extrapolate from elbow → wrist to estimate where the hidden
 * hand sits. Hand zone is a square of size ~1.3 × forearm length centered
 * on (wrist + 0.3 × (wrist - elbow)).
 */
function forwardExtrapolate(e: PoseLandmark, w: PoseLandmark): BBox {
  const dx = w.x - e.x;
  const dy = w.y - e.y;
  const forearm = Math.hypot(dx, dy) || 0.15;
  const cx = w.x + dx * 0.3;
  const cy = w.y + dy * 0.3;
  const half = forearm * 0.65;
  return {
    x: clamp01(cx - half),
    y: clamp01(cy - half),
    w: Math.min(1 - clamp01(cx - half), half * 2),
    h: Math.min(1 - clamp01(cy - half), half * 2),
  };
}

function narrate(p: ArmEstimate, armCount: number): string {
  const sideKo = p.side === 'left' ? '왼' : '오른';
  const both = armCount === 2 ? ' (양팔 관찰)' : '';
  switch (p.posture) {
    case 'extended_forward': return `손 직접 추적 불가 — ${sideKo}팔 책상 앞으로 뻗음으로 추정${both}`;
    case 'bent_writing':     return `손 직접 추적 불가 — ${sideKo}팔 글쓰기 자세로 추정${both}`;
    case 'raised':           return `손 직접 추적 불가 — ${sideKo}팔 위로 올림으로 추정${both}`;
    case 'lowered':          return `손 직접 추적 불가 — ${sideKo}팔 책상 아래로 내림으로 추정${both}`;
    default:                 return `손 직접 추적 불가 — ${sideKo}팔 위치만 관찰${both}`;
  }
}

function confidenceColor(c: number): ArmFallbackResult['uiColor'] {
  if (c >= 0.85) return 'yellow'; // 팔은 잘 보이지만 어쨌든 손 직접 아님
  if (c >= 0.65) return 'orange';
  return 'red';
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

// ─── Object × Arm cross-check ──────────────────────────────────
//
// 박하연 0513 자연스러운 확장: 손이 직접 안 잡혀도, **객체 detection bbox
// (휴대폰/책/노트북)와 추정 hand zone이 겹치면** "팔로 그 객체를 잡고
// 있을 가능성" 보강 시그널. 단정 ❌ — 거리/IoU + arm confidence로 출력.

export interface ObjectLikeBBox {
  /** Bbox in normalized [0,1] coords (Detection.bboxNorm 형태). */
  bboxNorm: { x: number; y: number; w: number; h: number };
  /** Display label (e.g., '핸드폰'). */
  labelKo: string;
  /** Salience classification ('high' / 'low' / 'none'). */
  salience: 'high' | 'low' | 'none';
  /** Detection confidence [0,1]. */
  score: number;
}

export interface ArmObjectIntersection {
  object: ObjectLikeBBox;
  /** IoU between estimated hand zone and object bbox. */
  iou: number;
  /** Center-to-center distance (normalized), 0 = same center. */
  centerDistance: number;
  /** narrator (중립 워딩). */
  narratorText: string;
  /** 0..1 — combines iou + (1 - centerDistance) + arm.confidence. */
  combinedConfidence: number;
}

/**
 * Look for detections that geometrically overlap the arm-fallback's
 * estimated hand zone. Only returns matches where the arm fallback is
 * active (i.e., hand is NOT directly detected) and the detection salience
 * is non-none. Sorted by combinedConfidence descending.
 */
export function intersectArmWithObjects(
  arm: ArmFallbackResult,
  detections: ObjectLikeBBox[],
): ArmObjectIntersection[] {
  if (!arm.active || !arm.primary) return [];
  const hand = arm.primary.estimatedHandZone;
  const results: ArmObjectIntersection[] = [];
  for (const det of detections) {
    if (det.salience === 'none') continue;
    const ioU = iouBBox(hand, det.bboxNorm);
    const hcx = hand.x + hand.w / 2;
    const hcy = hand.y + hand.h / 2;
    const ocx = det.bboxNorm.x + det.bboxNorm.w / 2;
    const ocy = det.bboxNorm.y + det.bboxNorm.h / 2;
    const centerDistance = Math.hypot(ocx - hcx, ocy - hcy);
    // Trigger: meaningful IoU OR centers close enough that the hand
    // (hidden but inferred) plausibly reaches the object.
    if (ioU < 0.03 && centerDistance > 0.25) continue;
    const proximity = 1 - Math.min(1, centerDistance / 0.4);
    const combinedConfidence = clamp01(
      0.4 * ioU + 0.4 * proximity + 0.2 * arm.primary.confidence,
    );
    results.push({
      object: det,
      iou: ioU,
      centerDistance,
      combinedConfidence,
      narratorText: narrateIntersection(arm.primary.posture, det.labelKo, combinedConfidence),
    });
  }
  results.sort((a, b) => b.combinedConfidence - a.combinedConfidence);
  return results;
}

function iouBBox(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const u = a.w * a.h + b.w * b.h - inter;
  return u > 0 ? inter / u : 0;
}

function narrateIntersection(
  posture: ArmPosture, labelKo: string, conf: number,
): string {
  const confLabel = conf >= 0.6 ? '높음' : conf >= 0.35 ? '보통' : '낮음';
  const postureKo =
    posture === 'extended_forward' ? '팔을 책상 앞으로 뻗은 자세'
    : posture === 'bent_writing' ? '글쓰기와 비슷한 자세'
    : posture === 'raised' ? '팔을 올린 자세'
    : posture === 'lowered' ? '팔을 책상 아래로 내린 자세'
    : '팔 자세';
  return `${labelKo} 객체가 추정 손 영역과 겹침 — ${postureKo}, 추정 신뢰도 ${confLabel}`;
}
